import type { Ref } from 'vue';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import { usePaymentError } from './usePaymentError';
import { usePaymentInit } from './usePaymentInit';
import { PAYMENT_INIT_ACTION, PAYMENT_SCRIPT_TIMEOUT_MS, PAYPAL_SCRIPT_SRC } from '../common/constants/paymentInit';
import { describeInitError } from '../utils/paymentInitError';
import { loadScript } from '../utils/paymentScripts';
import { rememberPaymentMethod } from '../utils/paymentMethodStorage';

interface PayPalInitResponse {
  link: string;
}

interface UsePayPalOptions {
  chargebeeId: () => string;
  discountPrice: () => number;
  roundedPrice: () => number;
  upsale: () => boolean;
  requestPayerEmail: () => boolean;
  containerRef: Ref<HTMLDivElement | undefined>;
  containerSelector: string;
  onClickButton: () => void;
  onSuccess: () => void;
  onAction: () => void;
}

export function usePayPal(options: UsePayPalOptions) {
  const {
    $paymentMethods,
    $logger,
  } = useNuxtApp();
  const { setUserProperty } = useMixpanel();
  const { reportPaymentError } = usePaymentError();
  const store = useKegelAnalyticStore();
  const API = useKegelApi();
  const { openError } = useAllModals();
  const { setLoader } = useDialogStore();
  const { roundNumber, paypalGatewayType } = useHelpers();
  const { track } = useEventsDispatcher();
  const { buyUpsale } = payments();
  const { registerPayerWithoutEmail, setEmailAfterPpPayment } = useEmail();

  const startTime = ref<number | null>(null);
  const authorizationEndTime = ref<number | null>(null);
  const paymentAcceptEndTime = ref<number | null>(null);
  const isShowFakeButton = ref(false);
  const isPayPalButtonRendered = ref(false);
  const rendered = ref(false);
  const {
    isCheckoutVariantB,
    isCheckoutVariantC,
    isCheckoutVariantD,
    isCheckoutVariantE,
  } = useCheckoutFormVariant();
  const payPalButtonStyle = computed(() => {
    return isCheckoutVariantB.value
      || isCheckoutVariantC.value
      || isCheckoutVariantD.value
      || isCheckoutVariantE.value
      ? { shape: 'rect' }
      : {};
  });

  function getStage(): 'Upsale' | 'Subscription' {
    return options.upsale() ? 'Upsale' : 'Subscription';
  }

  function setAuthorizationDurationIfStarted(): void {
    if (startTime.value !== null) {
      authorizationEndTime.value = (performance.now() - startTime.value) / 1000;
    }
  }

  function trackPaymentDuration(result: 'success' | 'error'): void {
    if (startTime.value === null) return;
    const finalTime = (performance.now() - startTime.value) / 1000;
    track('Payment duration', {
      type: $paymentMethods.paypal,
      gateway: paypalGatewayType.value,
      authorization_duration: roundNumber(authorizationEndTime.value),
      backend_payment_duration: roundNumber(paymentAcceptEndTime.value),
      total_duration: roundNumber(finalTime),
      result,
    });
  }

  const { isReady, isFailed, load } = usePaymentInit({
    action: PAYMENT_INIT_ACTION.PAYPAL,
    attemptTimeoutMs: PAYMENT_SCRIPT_TIMEOUT_MS,
    initialize: async (isCurrent) => {
      isShowFakeButton.value = true;
      isPayPalButtonRendered.value = false;

      try {
        await loadScript(PAYPAL_SCRIPT_SRC, PAYMENT_SCRIPT_TIMEOUT_MS);

        const data = await API.paypalInit(store.userUuid);

        if (!isCurrent()) return;

        await renderPayPalButton(data);
      } finally {
        isShowFakeButton.value = false;
      }
    },
    onGiveUp: (error) => {
      track('PayPal button no render', { message: describeInitError(error) });
    },
  });

  function handleButtonClick(): void {
    startTime.value = performance.now();
    setUserProperty({ gateway: 'paypal_express_checkout' });
    track('Authorization_Start', {
      type: $paymentMethods?.paypal,
      gateway: paypalGatewayType.value,
    });
    track('Check-out Started', {
      type: $paymentMethods?.paypal,
      gateway: paypalGatewayType.value,
    });
    setLoader(true);
    store.isDisabledBaseDialog = true;
    isShowFakeButton.value = true;
    options.onClickButton();
  }

  async function handleButtonAuthorize(authorizationData: any): Promise<void> {
    if (options.requestPayerEmail()) {
      await registerPayerWithoutEmail();
    }

    if (!authorizationData.billingToken) {
      $logger.note({
        category: 'payment',
        message: 'PayPal authorization returned no billing token',
        level: 'warning',
      });
    }

    setAuthorizationDurationIfStarted();
    processPayPal(authorizationData.billingToken);
  }

  function handleButtonCancel(): void {
    setLoader(false);
    store.isDisabledBaseDialog = false;
    isShowFakeButton.value = false;
  }

  function handleButtonError(errorData: ApiErrorModel): void {
    if (!authorizationEndTime.value) {
      setAuthorizationDurationIfStarted();
    }
    openError({ content: 'lp_v7_pop_up_payment_error_message' });
    setLoader(false);
    store.isDisabledBaseDialog = false;
    isShowFakeButton.value = false;
    reportPaymentError({
      error: errorData,
      stage: getStage(),
      type: $paymentMethods?.paypal,
    });
    trackPaymentDuration('error');
  }

  function buildPayPalButtonConfig(data: PayPalInitResponse) {
    const PAYPAL_MODE: string = useRuntimeConfig().public.PAYPAL_MODE;

    return {
      env: PAYPAL_MODE,
      commit: true,
      locale: 'en_US',
      style: {
        size: 'medium',
        shape: 'pill',
        label: 'buynow',
        branding: true,
        tagline: 'false',
        height: 55,
        ...payPalButtonStyle.value,
      },
      payment() {
        return data.link.split('=')[1];
      },
      onClick: handleButtonClick,
      onAuthorize: handleButtonAuthorize,
      onCancel: handleButtonCancel,
      onError: handleButtonError,
    };
  }

  async function renderPayPalButton(data: PayPalInitResponse): Promise<void> {
    if (!options.containerRef.value?.isConnected) return;
    if (rendered.value && options.containerRef.value.childElementCount) return;

    await (globalThis as any).paypal.Button.render(
      buildPayPalButtonConfig(data),
      options.containerSelector,
    );

    isPayPalButtonRendered.value = true;
    rendered.value = true;
  }

  function getPayPalAcceptPayload(token: string) {
    return {
      token_id: token,
      item: options.chargebeeId(),
      web_user_uuid: store.userUuid,
    };
  }

  async function acceptPayPalPayment(token: string) {
    if (options.upsale()) {
      return await buyUpsale({
        paypal_token_id: token,
        item: options.chargebeeId(),
        web_user_uuid: store.userUuid,
        stage: 'upsale',
        amount: options.roundedPrice(),
      });
    }

    return await API.payPalAccept(getPayPalAcceptPayload(token));
  }

  function handleProcessSuccess(): void {
    track('Authorization_Success', {
      type: $paymentMethods?.paypal,
      price: options.discountPrice(),
    });
    rememberPaymentMethod('PayPal');
    globalThis.document.body.classList.remove('remove');
    store.isDisabledBaseDialog = false;
    trackPaymentDuration('success');
    options.onSuccess();
  }

  function handleProcessError(error: any): void {
    isShowFakeButton.value = false;
    openError({ content: 'lp_v7_pop_up_payment_error_message' });
    setLoader(false);
    reportPaymentError({
      error: error as ApiErrorModel,
      stage: getStage(),
      type: $paymentMethods?.paypal,
    });
    trackPaymentDuration(error?.status === 409 ? 'success' : 'error');
    if (error?.status === 409) {
      options.onAction();
    }
  }

  async function processPayPal(token: string) {
    setLoader(true);
    try {
      const paymentAcceptStartTime = performance.now();
      const response = await acceptPayPalPayment(token);

      paymentAcceptEndTime.value = (performance.now() - paymentAcceptStartTime) / 1000;

      if (response?.email && options.requestPayerEmail()) {
        setEmailAfterPpPayment(response.email);
      }

      if (response && !response.email) {
        throw response;
      }

      handleProcessSuccess();
    } catch (error: any) {
      handleProcessError(error);
    } finally {
      setLoader(false);
    }
  }

  return {
    isShowFakeButton,
    isReady,
    isFailed,
    load,
    isPayPalButtonRendered,
  };
}
